import Keycloak from 'keycloak-js'

export interface RuntimeConfig {
  apiBaseUrl: string
  authRequired: boolean
  keycloakUrl: string
  keycloakRealm: string
  keycloakClientId: string
  // Canal de contato da oficina exibido quando o cliente recusa um reparo (ADR-0011).
  // shopWhatsapp: número em formato internacional só com dígitos (ex.: 5511999998888).
  shopWhatsapp: string | null
  shopPhone: string | null
}

export interface Identity {
  subject?: string
  name?: string
  email?: string
}

declare global {
  interface Window { __CK_CONFIG__?: Partial<RuntimeConfig> }
}

export const runtimeConfig: RuntimeConfig = {
  apiBaseUrl: window.__CK_CONFIG__?.apiBaseUrl ?? 'http://localhost:10246/api/v1',
  authRequired: window.__CK_CONFIG__?.authRequired ?? false,
  keycloakUrl: window.__CK_CONFIG__?.keycloakUrl ?? 'http://localhost:8080',
  keycloakRealm: window.__CK_CONFIG__?.keycloakRealm ?? 'ck-car-repair-dev',
  keycloakClientId: window.__CK_CONFIG__?.keycloakClientId ?? 'ck-cliente',
  shopWhatsapp: window.__CK_CONFIG__?.shopWhatsapp ?? null,
  shopPhone: window.__CK_CONFIG__?.shopPhone ?? null
}

const keycloak = new Keycloak({
  url: runtimeConfig.keycloakUrl,
  realm: runtimeConfig.keycloakRealm,
  clientId: runtimeConfig.keycloakClientId
})

let initialized = false

export async function initializeAuth(): Promise<void> {
  if (initialized || !runtimeConfig.authRequired) return
  try {
    await keycloak.init({
      onLoad: 'check-sso',
      flow: 'standard',
      pkceMethod: 'S256',
      redirectUri: `${window.location.origin}/auth/callback`,
      // O check silencioso usa um iframe de cookies de terceiros. Browsers como o
      // Edge podem bloquear requestStorageAccess fora de um gesto do usuário e
      // impedir que o bootstrap do portal seja concluído.
      checkLoginIframe: false
    })
  } catch (error) {
    // A aplicação ainda deve montar para exibir a tela de login. Nenhum dado
    // protegido é liberado: sem uma sessão válida, as rotas continuam bloqueadas.
    console.warn('[CK Auth] Não foi possível verificar a sessão automaticamente.', error)
  } finally {
    initialized = true
  }
}

export async function accessToken(): Promise<string | undefined> {
  if (!runtimeConfig.authRequired || !keycloak.authenticated) return undefined
  await keycloak.updateToken(30)
  return keycloak.token
}

export function isAuthenticated(): boolean {
  return !runtimeConfig.authRequired || keycloak.authenticated === true
}

export function hasRole(role: string): boolean {
  return !runtimeConfig.authRequired || keycloak.hasRealmRole(role)
}

export function login(options: { loginHint?: string; action?: string } = {}): Promise<void> {
  return keycloak.login({
    redirectUri: `${window.location.origin}/auth/callback`,
    loginHint: options.loginHint,
    action: options.action
  })
}

export function updatePassword(): Promise<void> {
  return login({ action: 'UPDATE_PASSWORD' })
}

export function forgotPassword(): void {
  const url = `${runtimeConfig.keycloakUrl}/realms/${runtimeConfig.keycloakRealm}/login-actions/reset-credentials` +
    `?client_id=${encodeURIComponent(runtimeConfig.keycloakClientId)}`
  window.location.assign(url)
}

export function identity(): Identity {
  const token = keycloak.tokenParsed as { sub?: string; name?: string; email?: string } | undefined
  return { subject: token?.sub, name: token?.name, email: token?.email }
}

export function logout(): Promise<void> {
  return keycloak.logout({ redirectUri: window.location.origin })
}
